/**
 * referral-detail-page.tsx — Detail view for a single pending referral.
 *
 * Route: /referral-queue/unknown-provider/:referralId
 *
 * Fetches referral via GET /admin/referrals/:referralId.
 * Displays provider info, source badge, and conditional action panel.
 *
 * Action buttons:
 *   awaiting_provider_review → Confirm Provider + Reject Referral
 *   ineligible               → Notify Provider
 *   rejected | eligibility_pending → no action buttons
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { useState, useEffect, type JSX } from 'react';
import { useParams, useNavigate } from 'react-router';

import { ConfirmProviderDialog } from './confirm-provider-dialog.js';
import { referralApi } from './referral-api.js';
import type { PendingReferralDetail } from './referral-api.js';
import { RejectReferralDialog } from './reject-referral-dialog.js';
import { useReferralActions } from './use-referral-actions.js';

type DialogOpen = 'confirm' | 'reject' | null;

const QUEUE_LIST_ROUTE = '/referral-queue/unknown-provider';

function SourceBadge({ source }: { source: 'nppes' | 'manual' }): JSX.Element {
  if (source === 'nppes') {
    return (
      <span className="inline-flex items-center rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        NPPES Lookup
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
      Manually Entered
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex gap-2 text-sm">
      <dt className="w-36 shrink-0 font-medium text-gray-600">{label}</dt>
      <dd className="text-gray-900">{value ?? '—'}</dd>
    </div>
  );
}

export function ReferralDetailPage(): JSX.Element {
  const { referralId } = useParams<{ referralId: string }>();
  const navigate = useNavigate();
  const auth = usePortalAuth();
  const actions = useReferralActions();
  const [detail, setDetail] = useState<PendingReferralDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogOpen>(null);
  const [notifyState, setNotifyState] = useState<{
    isPending: boolean;
    error: string | null;
    successBanner: string | null;
  }>({
    isPending: false,
    error: null,
    successBanner: null,
  });

  useEffect(() => {
    if (!referralId) return;
    setIsLoading(true);
    referralApi
      .getReferral(referralId, auth.user?.access_token)
      .then((d) => {
        setDetail(d);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : 'Failed to load referral.');
        setIsLoading(false);
      });
  }, [referralId, auth.user?.access_token]);

  async function handleNotifyIneligible(): Promise<void> {
    if (!referralId) return;
    setNotifyState({ isPending: true, error: null, successBanner: null });
    try {
      await actions.notifyIneligible.execute(referralId);
      setNotifyState({
        isPending: false,
        error: null,
        successBanner: 'Provider notified of ineligibility.',
      });
    } catch (err) {
      setNotifyState({
        isPending: false,
        error: err instanceof Error ? err.message : 'Notification failed.',
        successBanner: null,
      });
    }
  }

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading referral" className="py-8 text-center text-gray-500">
        Loading&hellip;
      </div>
    );
  }

  if (fetchError || !detail) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-600">{fetchError ?? 'Referral not found.'}</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 text-sm text-blue-600 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const isAwaitingReview = detail.status === 'awaiting_provider_review';
  const isIneligible = detail.status === 'ineligible';

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Referral Detail</h1>
        <button
          type="button"
          onClick={() => navigate(QUEUE_LIST_ROUTE)}
          className="text-sm text-blue-600 underline"
        >
          Back to queue
        </button>
      </div>

      {/* Provider Info */}
      <section className="rounded border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="font-medium text-gray-900">Provider Information</h2>
          <SourceBadge source={detail.providerSource} />
        </div>
        <dl className="space-y-2">
          <InfoRow
            label="Name"
            value={`${detail.provider.firstName} ${detail.provider.lastName}`}
          />
          <InfoRow label="NPI" value={detail.provider.npi} />
          <InfoRow label="Practice" value={detail.provider.practice} />
          <InfoRow label="Specialty" value={detail.provider.specialty} />
          <InfoRow label="Email" value={detail.provider.email} />
          <InfoRow label="Phone" value={detail.provider.phone} />
          <InfoRow label="Address" value={detail.provider.address} />
        </dl>
      </section>

      {/* Status-specific info */}
      {isIneligible && detail.cmsReasonCode && (
        <section className="rounded border border-red-200 bg-red-50 p-4">
          <h3 className="mb-1 font-medium text-red-800">CMS Ineligibility Reason</h3>
          <p className="font-mono text-sm text-red-700">{detail.cmsReasonCode}</p>
        </section>
      )}

      {detail.status === 'rejected' && detail.rejectionReason && (
        <section className="rounded border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-1 font-medium text-gray-700">Rejection Reason</h3>
          <p className="text-sm text-gray-800">{detail.rejectionReason}</p>
        </section>
      )}

      {/* Action Panel */}
      {isAwaitingReview && (
        <section className="flex gap-3">
          <button
            type="button"
            onClick={() => setDialog('confirm')}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Confirm Provider
          </button>
          <button
            type="button"
            onClick={() => setDialog('reject')}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Reject Referral
          </button>
        </section>
      )}

      {isIneligible && (
        <section className="flex flex-col gap-2">
          {notifyState.successBanner && (
            <p
              role="status"
              className="rounded bg-green-50 px-3 py-2 text-sm font-medium text-green-800"
            >
              {notifyState.successBanner}
            </p>
          )}
          {notifyState.error && (
            <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{notifyState.error}</p>
          )}
          <div>
            <button
              type="button"
              onClick={() => void handleNotifyIneligible()}
              disabled={notifyState.isPending}
              className="rounded bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {notifyState.isPending ? 'Notifying…' : 'Notify Provider'}
            </button>
          </div>
        </section>
      )}

      {/* Dialogs */}
      {dialog === 'confirm' && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Confirm Provider</h2>
            <ConfirmProviderDialog
              onConfirm={() => actions.confirmProvider.execute(detail.referralId)}
              onClose={() => setDialog(null)}
              onSuccess={() => {
                setDialog(null);
                void navigate(QUEUE_LIST_ROUTE, {
                  state: { successMessage: 'Provider confirmed. Eligibility check triggered.' },
                });
              }}
            />
          </div>
        </div>
      )}

      {dialog === 'reject' && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Reject Referral</h2>
            <RejectReferralDialog
              onReject={(reason) => actions.rejectReferral.execute(detail.referralId, reason)}
              onClose={() => setDialog(null)}
              onSuccess={() => {
                setDialog(null);
                void navigate(QUEUE_LIST_ROUTE, {
                  state: { successMessage: 'Referral rejected. Provider notified.' },
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
